import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';
import type { GetContentV1Output } from '../../lib/api/content-api-client';

/**
 * Content slice state (task 5.2, public-website spec).
 *
 * Per-date keyed cache of `GetContentV1` payloads per design §3.1
 * (Slices layer): one entry per requested date in `byDate`, plus a
 * parallel `statusByDate` and `errorByDate` so the UI can distinguish
 * "loading", "succeeded", and "failed" per date. The Daily and
 * Day-detail pages read entries via the selectors in 5.3 and dispatch
 * the matching thunk in `store/thunks/content-thunks.ts`.
 *
 * Reducers are pure — Redux Toolkit's `createSlice` wraps every
 * mutation in Immer so the assignments below are safe inside the
 * `produce` proxy and produce a new immutable state object.
 *
 * Function-length budget: every reducer below stays under 20 lines
 * per Requirement 3.7.
 */

/** Per-date fetch status discriminator. */
export type ContentFetchStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

/** Content slice state shape. */
export interface ContentState {
  /** Per-date cache of `GetContentV1` payloads, keyed by `YYYY-MM-DD`. */
  readonly byDate: Record<string, GetContentV1Output>;
  /** Per-date fetch status. Absent key = `idle`. */
  readonly statusByDate: Record<string, ContentFetchStatus>;
  /** Per-date last error message (cleared on subsequent request). */
  readonly errorByDate: Record<string, string | undefined>;
}

const initialState: ContentState = {
  byDate: {},
  statusByDate: {},
  errorByDate: {},
};

export const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    /** Mark a date as in-flight; clear any prior error. */
    contentFetchRequested(state, action: PayloadAction<{ date: string }>) {
      state.statusByDate[action.payload.date] = 'loading';
      state.errorByDate[action.payload.date] = undefined;
    },
    /** Cache the payload for a date and mark the fetch succeeded. */
    contentFetchSucceeded(
      state,
      action: PayloadAction<{ date: string; payload: GetContentV1Output }>,
    ) {
      state.byDate[action.payload.date] = castDraft(action.payload.payload);
      state.statusByDate[action.payload.date] = 'succeeded';
      state.errorByDate[action.payload.date] = undefined;
    },
    /** Record the failure message for a date. */
    contentFetchFailed(
      state,
      action: PayloadAction<{ date: string; error: string }>,
    ) {
      state.statusByDate[action.payload.date] = 'failed';
      state.errorByDate[action.payload.date] = action.payload.error;
    },
  },
});

export const {
  contentFetchRequested,
  contentFetchSucceeded,
  contentFetchFailed,
} = contentSlice.actions;

export default contentSlice.reducer;
